import { PolygonSchema } from '../../types/maps';
import {
  polygonAreaSqMeters,
  areaSqMetersToHectares,
  areaSqMetersToKm2,
} from './geoGeometryUtils';
import { FileExtensions } from '../../constants/geolocation';

/**
 * Interface for GeoJSON Feature properties with metadata
 */
interface PolygonFeatureProperties {
  name?: string;
  area_hectares: number;
  area_km2: number;
  area_m2: number;
}

/**
 * Exports polygons to GeoJSON FeatureCollection format.
 * Includes WGS84 (EPSG:4326) CRS and polygon area metadata.
 *
 * @param polygons - Array of PolygonSchema (where each PolygonSchema is LatLng[])
 * @param names - Optional array of polygon names (parallel to polygons)
 * @returns Blob containing the GeoJSON file
 */
export function exportToGeoJSON(
  polygons: PolygonSchema[],
  names?: string[]
): Blob {
  const features = polygons.map((polygon, index) => {
    // Calculate polygon area
    const areaSqM = polygonAreaSqMeters(polygon);
    const areaHa = areaSqMetersToHectares(areaSqM);
    const areaKm2 = areaSqMetersToKm2(areaSqM);

    // Convert LatLng to GeoJSON coordinates [lng, lat]
    const coordinates = polygon.map(coord => [coord.lng, coord.lat]);

    // Close the polygon ring by adding first point at the end
    coordinates.push(coordinates[0]);

    const properties: PolygonFeatureProperties = {
      area_hectares: parseFloat(areaHa.toFixed(4)),
      area_km2: parseFloat(areaKm2.toFixed(6)),
      area_m2: parseFloat(areaSqM.toFixed(2)),
    };

    // Add name if provided
    if (names && names[index]) {
      properties.name = names[index];
    }

    return {
      type: 'Feature',
      properties,
      geometry: {
        type: 'Polygon',
        coordinates: [coordinates], // Outer ring only
      },
    };
  });

  const geoJson = {
    type: 'FeatureCollection',
    crs: {
      type: 'name',
      properties: {
        name: 'EPSG:4326', // WGS84
      },
    },
    features,
  };

  const jsonString = JSON.stringify(geoJson, null, 2);
  return new Blob([jsonString], { type: 'application/geo+json' });
}

/**
 * Generates a complete filename with extension for GeoJSON export
 *
 * @param baseName - Base name without extension
 * @returns Complete filename with .geojson extension
 */
export function getGeoJSONFileName(baseName: string): string {
  return `${baseName}${FileExtensions.GEOJSON}`;
}
